import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Send, Loader2, CheckCircle2 } from "lucide-react";
import { AUDIT_FORM } from "@/constants/testIds";
import { submitAuditRequest } from "@/lib/api";
import { trackLead } from "@/lib/analytics";

const initial = {
  business_name: "",
  website_or_profile: "",
  email: "",
  phone: "",
  problem_description: "",
};

export default function AuditForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.business_name.trim()) return "Business name is required.";
    if (!form.website_or_profile.trim()) return "Website or Google Business link is required.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) return "Enter a valid email.";
    if (!form.problem_description.trim()) return "Tell us briefly about the reviews.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      await submitAuditRequest(form);
      trackLead("audit_form", { business_name: form.business_name });
      setSubmitted(true);
      toast.success("Audit request received. We'll reply within one business day.");
      setForm(initial);
    } catch (e2) {
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="audit" className="relative py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-[#10B981]/[0.06] via-white/[0.02] to-transparent overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 opacity-40 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(600px 300px at 80% 20%, rgba(16,185,129,0.15), transparent 60%), radial-gradient(500px 300px at 10% 90%, rgba(212,175,55,0.10), transparent 60%)",
            }}
          />

          <div className="relative grid lg:grid-cols-12 gap-10 p-8 lg:p-14">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                Free reputation audit
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 leading-[1.05]">
                See exactly which reviews qualify for removal.
              </h2>
              <p className="mt-5 text-white/60 text-sm sm:text-base leading-relaxed max-w-md">
                Tell us about your Google Business Profile and the reviews hurting your reputation.
                Within one business day, you'll have a full policy-mapped audit — no cost, no obligation.
              </p>

              <ul className="mt-8 space-y-3 text-sm text-white/75">
                {[
                  "Zero cost, no credit card",
                  "Policy-mapped list of removable reviews",
                  "Estimated timeline & recovery plan",
                  "Pay $195 only after Google confirms removal",
                ].map((b) => (
                  <li key={b} className="flex items-center gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                    {b}
                  </li>
                ))}
              </ul>

              <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#10B981]/30 bg-[#10B981]/10 px-3 py-1.5 text-xs text-[#10B981] font-semibold">
                <ShieldCheck className="w-3.5 h-3.5" />
                No removal, no charge — guaranteed
              </div>

              <div className="mt-6 flex items-center gap-3">
                <div className="flex -space-x-2">
                  {["#10B981", "#D4AF37", "#94A3B8", "#0ea774"].map((c, i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full border-2 border-[#0d1424] grid place-items-center text-[10px] font-display font-bold text-white/90"
                      style={{ backgroundColor: c }}
                    >
                      {["MA", "PS", "JW", "LC"][i]}
                    </div>
                  ))}
                </div>
                <div className="text-xs text-white/60 leading-tight">
                  Join <span className="font-semibold text-white">640+ business owners</span>
                  <br />
                  who've protected their reputation.
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              {submitted ? (
                <div
                  data-testid={AUDIT_FORM.success}
                  className="rounded-2xl border border-[#10B981]/40 bg-[#10B981]/10 p-8 flex flex-col items-start gap-4"
                >
                  <div className="w-12 h-12 grid place-items-center rounded-full bg-[#10B981] text-white">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <h3 className="font-display text-2xl font-bold">Audit request received.</h3>
                  <p className="text-white/70 text-sm max-w-md">
                    A reputation specialist will review your Google Business Profile and reply within
                    one business day with a policy-mapped list of removal-eligible reviews.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-[#10B981] hover:text-[#0ea774] text-sm font-semibold transition-colors"
                  >
                    Submit another →
                  </button>
                </div>
              ) : (
                <motion.form
                  data-testid={AUDIT_FORM.root}
                  onSubmit={onSubmit}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5 }}
                  className="rounded-2xl border border-white/10 bg-[#0d1424]/70 backdrop-blur-xl p-6 sm:p-8 space-y-5"
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Business name">
                      <input
                        data-testid={AUDIT_FORM.businessName}
                        value={form.business_name}
                        onChange={onChange("business_name")}
                        placeholder="Riverside Bistro"
                        className="input"
                      />
                    </Field>
                    <Field label="Website or Google Business link">
                      <input
                        data-testid={AUDIT_FORM.websiteOrProfile}
                        value={form.website_or_profile}
                        onChange={onChange("website_or_profile")}
                        placeholder="riversidebistro.com or g.page/..."
                        className="input"
                      />
                    </Field>
                    <Field label="Email">
                      <input
                        type="email"
                        data-testid={AUDIT_FORM.email}
                        value={form.email}
                        onChange={onChange("email")}
                        placeholder="you@business.com"
                        className="input"
                      />
                    </Field>
                    <Field label="Phone (optional)">
                      <input
                        data-testid={AUDIT_FORM.phone}
                        value={form.phone}
                        onChange={onChange("phone")}
                        placeholder="(555) 123-4567"
                        className="input"
                      />
                    </Field>
                  </div>

                  <Field label="Briefly describe the review problem">
                    <textarea
                      data-testid={AUDIT_FORM.problem}
                      value={form.problem_description}
                      onChange={onChange("problem_description")}
                      rows={4}
                      placeholder="e.g. 4 negative reviews from people who never visited, and a spam attack from a competitor over the last month."
                      className="input resize-none"
                    />
                  </Field>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid={AUDIT_FORM.submit}
                    className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#10B981] hover:bg-[#0ea774] text-white font-semibold text-sm transition-colors shadow-lg shadow-emerald-500/30 disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      <>
                        Request my free audit
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                  <p className="text-[11px] text-white/40 text-center">
                    By submitting, you agree to our privacy policy. We never share your info.
                  </p>
                </motion.form>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.1);
          color: white;
          border-radius: 10px;
          padding: 12px 14px;
          font-size: 14px;
          transition: border-color .15s, background-color .15s, box-shadow .15s;
          outline: none;
        }
        .input::placeholder { color: rgba(255,255,255,0.35); }
        .input:focus {
          border-color: rgba(16,185,129,0.6);
          background: rgba(16,185,129,0.05);
          box-shadow: 0 0 0 3px rgba(16,185,129,0.15);
        }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-semibold mb-1.5">
        {label}
      </div>
      {children}
    </label>
  );
}
